import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../store';
import {
    fetchEmailDomains,
    fetchGoDaddyDomains,
    addEmailDomain,
    deleteEmailDomain,
    checkEmailDomainStatus,
    createSubdomain
} from '../store/slices/domainSlice';
import { addNotification, showModal } from '../store/slices/uiSlice';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { FiPlus, FiRefreshCw, FiCheck, FiX, FiTrash2, FiGlobe, FiMail, FiList } from 'react-icons/fi';
import AddSubdomainForm from '../components/domain/AddSubdomainForm';

// Validation schema for adding a domain
const DomainSchema = Yup.object().shape({
    domain: Yup.string()
        .matches(
            /^[a-zA-Z0-9][a-zA-Z0-9-]{0,61}[a-zA-Z0-9](?:\.[a-zA-Z]{2,})+$/,
            'Enter a valid domain'
        )
        .required('Domain is required'),
});

const DomainsPage: React.FC = () => {
    const dispatch = useDispatch<AppDispatch>();
    const { emailDomains, godaddyDomains, loading, error } = useSelector((state: RootState) => state.domains);
    const [addingDomain, setAddingDomain] = useState(false);
    const [activeTab, setActiveTab] = useState('email');
    const [addingSubdomain, setAddingSubdomain] = useState(false);
    const [activeDomain, setActiveDomain] = useState<string | null>(null);

    const handleShowAddSubdomainForm = (domain: string) => {
        // Only allow for ontoris.com domain
        if (domain === 'ontoris.com') {
            setActiveDomain(domain);
            setAddingSubdomain(true);
        } else {
            dispatch(
                addNotification({
                    type: 'warning',
                    title: 'Feature Restricted',
                    message: 'Adding subdomains is only available for ontoris.com',
                })
            );
        }
    };

    const handleAddSubdomain = async (values: any) => {
        
        if (!activeDomain) return;

        try {
            // Log the data being sent for debugging
          

            await dispatch(createSubdomain({
                parentDomain: activeDomain,
                subdomain: values.subdomain,
                dnsData: values.dnsData
            })).unwrap();

            dispatch(
                addNotification({
                    type: 'success',
                    message: `Subdomain "${values.subdomain}.${activeDomain}" was successfully added.`,
                })
            );

            setAddingSubdomain(false);
            setActiveDomain(null);

            // Refresh the domains list
            dispatch(fetchGoDaddyDomains());
        } catch (error: any) {
            dispatch(
                addNotification({
                    type: 'error',
                    title: 'Subdomain Addition Failed',
                    message: error.message || 'Failed to add subdomain.',
                })
            );
        }
    };

    const handleViewDnsRecords = (domain: string) => {
        // Implement a DNS records viewer
        dispatch(
            showModal({
                title: `DNS Records for ${domain}`,
                content: <div>DNS Records viewer will be displayed here</div>,
                confirmLabel: 'Close',
                hideCancel: true,
                size: 'lg'
            })
        );
    };

    useEffect(() => {
        const loadData = async () => {
            try {
                if (activeTab === 'email') {
                    await dispatch(fetchEmailDomains());
                } else if (activeTab === 'godaddy') {
                    await dispatch(fetchGoDaddyDomains());
                }
            } catch (error) {
                console.error('Error loading domain data:', error);
            }
        };

        loadData();
    }, [dispatch, activeTab]);

    const handleAddDomain = async (values: any, { resetForm }: any) => {
        try {
            await dispatch(addEmailDomain(values.domain)).unwrap();

            dispatch(
                addNotification({
                    type: 'success',
                    message: `Domain "${values.domain}" was successfully added.`,
                })
            );

            resetForm();
            setAddingDomain(false);

            // Refresh the domains list
            dispatch(fetchEmailDomains());
        } catch (error: any) {
            dispatch(
                addNotification({
                    type: 'error',
                    title: 'Domain Addition Failed',
                    message: error.message || 'Failed to add domain.',
                })
            );
        }
    };

    const handleDeleteDomain = (domain: string) => {
        dispatch(
            showModal({
                title: 'Confirm Domain Deletion',
                content: `Are you sure you want to delete domain "${domain}"? This action cannot be undone.`,
                confirmLabel: 'Delete',
                cancelLabel: 'Cancel',
                onConfirm: async () => {
                    try {
                        await dispatch(deleteEmailDomain(domain)).unwrap();

                        dispatch(
                            addNotification({
                                type: 'success',
                                message: `Domain "${domain}" was successfully deleted.`,
                            })
                        );

                        // Refresh the domains list
                        dispatch(fetchEmailDomains());
                    } catch (error: any) {
                        dispatch(
                            addNotification({
                                type: 'error',
                                title: 'Domain Deletion Failed',
                                message: error.message || 'Failed to delete domain.',
                            })
                        );
                    }
                },
            })
        );
    };

    const handleCheckStatus = async (domain: string) => {
        try {
            const result = await dispatch(checkEmailDomainStatus(domain)).unwrap();

            dispatch(
                showModal({
                    title: 'Domain Verification Status',
                    content: (
                        <div className="space-y-4">
                            <div className="flex items-center">
                                <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full mr-2 ${result.verificationStatus === 'Verified'
                                        ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300'
                                        : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300'
                                    }`}>
                                    {result.verificationStatus}
                                </span>
                                <span className="text-gray-700 dark:text-gray-300">Overall Verification</span>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">DKIM Verification</h4>
                                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full mt-1 ${result.dkimVerificationStatus === 'Verified'
                                            ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300'
                                            : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300'
                                        }`}>
                                        {result.dkimVerificationStatus}
                                    </span>
                                </div>

                                <div>
                                    <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">SPF Verification</h4>
                                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full mt-1 ${result.spfVerificationStatus === 'Verified'
                                            ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300'
                                            : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300'
                                        }`}>
                                        {result.spfVerificationStatus}
                                    </span>
                                </div>

                                <div>
                                    <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">MX Verification</h4>
                                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full mt-1 ${result.mxVerificationStatus === 'Verified'
                                            ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300'
                                            : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300'
                                        }`}>
                                        {result.mxVerificationStatus}
                                    </span>
                                </div>
                            </div>

                            <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
                                If verification is still pending, please ensure all DNS records have been properly configured.
                            </p>
                        </div>
                    ),
                    confirmLabel: 'Close',
                    hideCancel: true,
                })
            );
        } catch (error: any) {
            dispatch(
                addNotification({
                    type: 'error',
                    title: 'Verification Check Failed',
                    message: error.message || 'Failed to check domain verification status.',
                })
            );
        }
    };

    return (
        <div className="space-y-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
                <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Domains Management</h1>

                {activeTab === 'email' && !addingDomain && !addingSubdomain && (
                    <button
                        onClick={() => setAddingDomain(true)}
                        className="mt-3 sm:mt-0 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                    >
                        <FiPlus className="mr-2" />
                        Add Email Domain
                    </button>
                )}

                {activeTab === 'godaddy' && !addingDomain && !addingSubdomain && (
                    <button
                        onClick={() => handleShowAddSubdomainForm('ontoris.com')}
                        className="mt-3 sm:mt-0 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                    >
                        <FiPlus className="mr-2" />
                        Add Ontoris Subdomain
                    </button>
                )}
            </div>

            {error && (
                <div className="bg-red-50 dark:bg-red-900 border border-red-200 dark:border-red-800 rounded-md p-4 text-sm text-red-600 dark:text-red-300">
                    {error}
                </div>
            )}

            {/* Tabs */}
            <div className="border-b border-gray-200 dark:border-gray-700">
                <nav className="-mb-px flex space-x-8" aria-label="Tabs">
                    <button
                        onClick={() => setActiveTab('email')}
                        className={`whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm ${activeTab === 'email'
                                ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-200 dark:hover:border-gray-600'
                            }`}
                    >
                        <div className="flex items-center">
                            <FiMail className="mr-2" />
                            Email Domains
                        </div>
                    </button>
                    <button
                        onClick={() => setActiveTab('godaddy')}
                        className={`whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm ${activeTab === 'godaddy'
                                ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-200 dark:hover:border-gray-600'
                            }`}
                    >
                        <div className="flex items-center">
                            <FiGlobe className="mr-2" />
                            GoDaddy Domains
                        </div>
                    </button>
                </nav>
            </div>

            {/* Add Domain Form */}
            {activeTab === 'email' && addingDomain && (
                <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
                    <h2 className="text-xl font-medium text-gray-900 dark:text-white mb-4">
                        Add Email Domain
                    </h2>

                    <Formik
                        initialValues={{ domain: '' }}
                        validationSchema={DomainSchema}
                        onSubmit={handleAddDomain}
                    >
                        {({ isValid, dirty }) => (
                            <Form className="space-y-4">
                                <div>
                                    <label htmlFor="domain" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                        Domain
                                    </label>
                                    <div className="mt-1">
                                        <Field
                                            type="text"
                                            name="domain"
                                            id="domain"
                                            placeholder="example.com"
                                            className="shadow-sm focus:ring-primary-500 focus:border-primary-500 block w-full sm:text-sm border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md"
                                        />
                                    </div>
                                    <ErrorMessage name="domain" component="div" className="mt-1 text-sm text-red-600 dark:text-red-400" />
                                </div>

                                <div className="flex justify-end space-x-3">
                                    <button
                                        type="button"
                                        onClick={() => setAddingDomain(false)}
                                        className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                                    >
                                        <FiX className="mr-2" />
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        disabled={!(isValid && dirty) || loading}
                                        className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:bg-primary-400 disabled:cursor-not-allowed"
                                    >
                                        {loading ? (
                                            <>
                                                <svg
                                                    className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                                                    xmlns="http://www.w3.org/2000/svg"
                                                    fill="none"
                                                    viewBox="0 0 24 24"
                                                >
                                                    <circle
                                                        className="opacity-25"
                                                        cx="12"
                                                        cy="12"
                                                        r="10"
                                                        stroke="currentColor"
                                                        strokeWidth="4"
                                                    ></circle>
                                                    <path
                                                        className="opacity-75"
                                                        fill="currentColor"
                                                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                                    ></path>
                                                </svg>
                                                Adding...
                                            </>
                                        ) : (
                                            <>
                                                <FiCheck className="mr-2" />
                                                Add Domain
                                            </>
                                        )}
                                    </button>
                                </div>
                            </Form>
                        )}
                    </Formik>
                </div>
            )}

            {/* Add Subdomain Form */}
            {activeTab === 'godaddy' && addingSubdomain && activeDomain && (
                <AddSubdomainForm
                    domain={activeDomain}
                    onSubmit={handleAddSubdomain}
                    onCancel={() => {
                        setAddingSubdomain(false);
                        setActiveDomain(null);
                    }}
                    loading={loading}
                />
            )}

            {/* Domain Lists */}
            <div className="bg-white dark:bg-gray-800 shadow rounded-lg overflow-hidden">
                {activeTab === 'email' ? (
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                            <thead className="bg-gray-50 dark:bg-gray-700">
                                <tr>
                                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                        Domain
                                    </th>
                                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                        Status
                                    </th>
                                    <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                        Actions
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                                {loading && !emailDomains.length ? (
                                    <tr>
                                        <td colSpan={3} className="px-6 py-4 text-center">
                                            <div className="flex justify-center">
                                                <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-primary-500"></div>
                                            </div>
                                        </td>
                                    </tr>
                                ) : emailDomains.length === 0 ? (
                                    <tr>
                                        <td colSpan={3} className="px-6 py-4 text-center text-sm text-gray-500 dark:text-gray-400">
                                            No email domains configured. Add a domain to get started.
                                        </td>
                                    </tr>
                                ) : (
                                    emailDomains.map((domain) => (
                                        <tr key={domain.name}>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="flex items-center">
                                                    <FiMail className="flex-shrink-0 mr-2 h-5 w-5 text-gray-400" />
                                                    <div className="text-sm font-medium text-gray-900 dark:text-white">{domain.name}</div>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${domain.verificationStatus === 'Verified'
                                                        ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300'
                                                        : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300'
                                                    }`}>
                                                    {domain.verificationStatus || domain.status}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                                <div className="flex justify-end space-x-2">
                                                    <button
                                                        onClick={() => handleCheckStatus(domain.name)}
                                                        className="text-primary-600 hover:text-primary-900 dark:text-primary-400 dark:hover:text-primary-300"
                                                        title="Check verification status"
                                                    >
                                                        <FiRefreshCw className="h-5 w-5" />
                                                    </button>
                                                    <button
                                                        onClick={() => handleDeleteDomain(domain.name)}
                                                        className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300"
                                                        title="Delete domain"
                                                    >
                                                        <FiTrash2 className="h-5 w-5" />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                            <thead className="bg-gray-50 dark:bg-gray-700">
                                <tr>
                                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                        Domain
                                    </th>
                                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                        Status
                                    </th>
                                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                        Expiration
                                    </th>
                                    <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                        Actions
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                                {loading && !godaddyDomains.length ? (
                                    <tr>
                                        <td colSpan={4} className="px-6 py-4 text-center">
                                            <div className="flex justify-center">
                                                <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-primary-500"></div>
                                            </div>
                                        </td>
                                    </tr>
                                ) : godaddyDomains.length === 0 ? (
                                    <tr>
                                        <td colSpan={4} className="px-6 py-4 text-center text-sm text-gray-500 dark:text-gray-400">
                                            No GoDaddy domains found. Domains registered with GoDaddy will appear here.
                                        </td>
                                    </tr>
                                ) : (
                                    godaddyDomains.map((domain) => (
                                        <tr key={domain.domain}>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="flex items-center">
                                                    <FiGlobe className="flex-shrink-0 mr-2 h-5 w-5 text-gray-400" />
                                                    <div className="text-sm font-medium text-gray-900 dark:text-white">{domain.domain}</div>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${domain.status === 'ACTIVE'
                                                        ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300'
                                                        : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300'
                                                    }`}>
                                                    {domain.status}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                                                {new Date(domain.expirationDate).toLocaleDateString()}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                                <div className="flex justify-end space-x-2">
                                                    {domain.domain === 'ontoris.com' && (
                                                        <button
                                                            onClick={() => handleShowAddSubdomainForm(domain.domain)}
                                                            className="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300"
                                                            title="Add subdomain"
                                                        >
                                                            <FiPlus className="h-5 w-5" />
                                                        </button>
                                                    )}
                                                    <button
                                                        onClick={() => handleViewDnsRecords(domain.domain)}
                                                        className="text-green-600 hover:text-green-900 dark:text-green-400 dark:hover:text-green-300"
                                                        title="View DNS records"
                                                    >
                                                        <FiList className="h-5 w-5" />
                                                    </button>
                                                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${domain.renewable
                                                            ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300'
                                                            : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
                                                        }`}>
                                                        {domain.renewable ? 'Renewable' : 'Not Renewable'}
                                                    </span>
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
};

export default DomainsPage;